<template>
  <form class="form" novalidate @submit.prevent="transferParticipant()">
    <Fieldset v-if="participant?.firstName" :title="t('participants.participant')">
      <ul>
        <li><strong>{{ t('participants.firstName') }}:</strong> {{ participant.firstName }}</li>
        <li><strong>{{ t('participants.lastName') }}:</strong> {{ participant.lastName }}</li>
        <li><strong>{{ t('participants.email') }}:</strong> {{ participant.emailAddress }}</li>
      </ul>
    </Fieldset>
    <Loader v-else :is-inline="true"/>

    <template v-if="activitiesOptions.length">
      <Fieldset>
        <FormErrorsList v-if="errorsMessages.length"
                        :errorsList="errorsMessages"
                        :title="t('global.formErrorNotification')"/>

        <FormRow>
          <FormSearchableSelect
              v-model="selectedActivityId"
              :label="t('activities.activity')"
              :options="activitiesOptions"
              :rules="[]"
              name="activityId"/>

          <FormSearchableSelect
              v-if="activityOccurrencesOptions.length"
              v-model="selectedActivityOccurrenceId"
              :label="t('activityOccurrences.occurrence')"
              :options="activityOccurrencesOptions"
              :rules="[]"
              name="activityOccurrenceId"/>
        </FormRow>
      </Fieldset>
      <div class="form__submit">
        <button
            :disabled="transferButtonIsDisabled"
            class="btn btn--fullscreen"
            type="submit">{{ t('global.transfer') }}
        </button>
      </div>
    </template>
    <p v-else>{{ t('activities.noAvailableActivity') }}</p>
  </form>
</template>

<script lang="ts" setup>
import {computed, ComputedRef, onMounted, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";
import {FormOption, Guid, TinyActivity} from "@/types";
import {useActivityOccurrenceService, useActivityService} from "@/inversify.config";
import {useRouter} from "vue-router";
import {notifyError, notifySuccess} from "@/notify";
import {Participant} from "@/types/entities/activity";
import {SucceededOrNotResponse} from "@/types/responses";
import {ActivityOccurrence, BaseActivityOccurrence} from "@/types/entities/activity/activityOccurrence";
import Loader from "@/components/layout/Loader.vue";
import FormSearchableSelect from "@/components/forms/FormSearchableSelect.vue";

// eslint-disable-next-line no-undef
const props = defineProps<{
  activityOccurrenceId: string
  participantId: string
}>()

const {t} = useI18n();

const router = useRouter()

const activityService = useActivityService()
const activityOccurenceService = useActivityOccurrenceService();

const selectedActivityId = ref("")
const activities = ref<TinyActivity[]>(await getAllActivitiesExceptCurrent() ?? [])
const selectedActivity = ref<TinyActivity>({activityOccurrences: []})

const activityOccurrences = ref<BaseActivityOccurrence[]>(selectedActivity.value ? getActivityOccurrencesExceptCurrent() : [])
const selectedActivityOccurrence = ref<BaseActivityOccurrence>(activityOccurrences.value.length > 0
    ? activityOccurrences.value[0] as BaseActivityOccurrence
    : {} as BaseActivityOccurrence)
const selectedActivityOccurrenceId = ref(selectedActivityOccurrence.value.id ?? "")

const participant = ref<Participant>({reducedMobility: false});

onMounted(async () => {
  await fetchActivityOccurrenceParticipant()
})

let activitiesOptions: ComputedRef<FormOption[]> = computed(() => {
  let formOptions = activities.value
      .filter(x => x && x.id && x.nameFr)
      .map(({id, nameFr}) => ({
        name: id,
        label: nameFr
      })) as FormOption[]
  return formOptions.sortByLabel()
});

let activityOccurrencesOptions: ComputedRef<FormOption[]> = computed(() => {
  if (activityOccurrences.value.length === 0)
    return []
  return activityOccurrences.value
      .filter(x => x && x.id && x.startDate)
      .map(x => {
        let startDate = x.startDate ? x.startDate : ""
        let endDate = x.endDate ? ` ---> ${x.endDate}` : ''
        return {
          name: x.id,
          label: `${startDate}${endDate}`
        }
      }) as FormOption[];
});

const transferButtonIsDisabled = computed(() => {
  return !Guid.isValid(selectedActivityId.value) || !Guid.isValid(selectedActivityOccurrenceId.value)
})

watch(selectedActivityId, (newId) => {
  selectedActivity.value = activities.value.find(x => x.id == newId) as TinyActivity

  activityOccurrences.value = selectedActivity.value.activityOccurrences
  if (activityOccurrences.value.length === 0)
    return
  selectedActivityOccurrence.value = activityOccurrences.value[0] as ActivityOccurrence
  selectedActivityOccurrenceId.value = activityOccurrences.value[0].id as string
})

async function fetchActivityOccurrenceParticipant() {
  let response = await activityOccurenceService.getActivityOccurrenceParticipant(props.participantId)
  if (!Guid.isValid(response.id ?? "")) {
    displayErrors(response as SucceededOrNotResponse)
    return
  }
  participant.value = response
}

async function transferParticipant() {
  let result = await activityOccurenceService.transferActivityOccurrenceParticipant(selectedActivityOccurrenceId.value, props.participantId)
  if (result?.succeeded) {
    notifySuccess(t('participants.form.transferSaveSuccess'))
    await router.push({name: "admin.children.activities.index"})
  } else {
    displayErrors(result)
    notifyError(t('participants.form.transferSaveError'))
  }
}

async function getAllActivitiesExceptCurrent() {
  const activities = await activityService.getAllActivities()
  // show activity if it has more than one occurrence, otherwise hide it since we don't want to transfer participant to same occurrence
  return activities.filter(x => !x.activityOccurrences.some(y => y.id == props.activityOccurrenceId) || x.activityOccurrences.length > 1)
}

function getActivityOccurrencesExceptCurrent() {
  return selectedActivity.value.activityOccurrences.filter(x => x.id != props.activityOccurrenceId)
}

const errorsMessages = ref<string[]>([])

function displayErrors(response: SucceededOrNotResponse) {
  response = new SucceededOrNotResponse(response.succeeded, response.errors)
  if (response?.succeeded != undefined && !response?.succeeded) {
    errorsMessages.value = response.getErrorMessages('validation.activityOccurrence')
  } else errorsMessages.value = []
}
</script>